<template>
  <div class="login-page">
    <section class="dune-container">
      <img :src="duneImage" alt="dunes background" />
    </section>
    <section class="header">
      <div class="login-screenshot-container">
        <img :src="abtestScreenshotImage" class="login-screenshot-top" alt="first interface screenshot" />
        <img :src="segmentsScreenshotImage" class="login-screenshot-bottom" alt="second interface screenshot" />
      </div>
      <div class="login-section">
        <VoodooTuneLogo class="voodootune-logo" />
        <p v-if="unauthorized" class="unauthorized-sentence">
          You don't have access to VoodooTune. Please
          <a href="https://voodoo-team.slack.com/archives/C010WK4U1BJ">contact us here</a>
          to be granted access.
        </p>
        <p v-else class="login-sentence">
          VoodooTune is a platform that allows to interact and perform several actions on already published games.
        </p>
        <vd-button v-if="showLogin" class="connect-button" type="primary" @click="login">
          <span v-if="unauthorized">Log in again</span>
          <span v-else>Sign in</span>
        </vd-button>
      </div>
    </section>
    <section class="login-characters-img">
      <img :src="undrawCodeReviewImage" />
      <img :src="undrawStatisticsImage" />
    </section>
    <section v-if="!unauthorized" class="more-block">
      <span class="more-infos">MORE INFO</span>
      <vd-custom-icon class="dropdown-icon" name="dropdown-icon" width="16" active />
      <span class="use-case-sentence">VoodooTune fills several use cases</span>
    </section>
    <section v-if="!unauthorized" class="use-cases">
      <div class="explanation">
        <h3>Remote settings</h3>
        <p>
          With VoodooTune, you can update your game configuration on-the-fly without the need to re-publish your game.
          For instance, you can increase the difficulty of your game or change the advertising frequency.
        </p>
      </div>
      <div class="explanation">
        <h3>A configuration for a specific population</h3>
        <p>
          VoodooTune allows you to create segments. Do you have a configuration that only applies to US iOS users? No
          problem: create a segment for users who live in the United States and use iOS. Then apply your configuration
          for this specific population.
        </p>
      </div>
      <div class="explanation">
        <h3>A/B Testing</h3>
        <p>
          With VoodooTune, you can perform AB tests on one or several segments. By analyzing the results of your test
          thanks to VoodooAnalytics, you will be able to determine if a configuration is better than another on a
          specified population of users.
        </p>
      </div>
      <div class="explanation">
        <h3>Customize your game for specific events</h3>
        <p>
          Is it Christmas or Halloween? You can change your characters' skin for this period without the hassle of
          passing through the AppStore validation. Is it Chinese New Year? Customize your assets only for a specific
          segment.
        </p>
      </div>
      <div class="explanation">
        <h3>Great for development</h3>
        <p>
          Game developers sometimes need to test multiple configurations on their devices to optimize the gameplay. With
          VoodooTune, no need to recompile the whole game each time a value is changed. Create the configuration on
          VoodooTune (in WIP) and use the VoodooSauce debugger on your device to test it.
        </p>
      </div>
      <div class="explanation">
        <h3>More features will come soon…</h3>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import VoodooTuneLogo from "@/assets/VoodooTuneLogo.vue";
import useAuthentication from "@/hooks/use-authentication";

import abtestScreenshotImage from "./assets/abtest-screenshot.png";
import duneImage from "./assets/dune.svg";
import segmentsScreenshotImage from "./assets/segments-screenshot.png";
import undrawCodeReviewImage from "./assets/undraw-code-review.svg";
import undrawStatisticsImage from "./assets/undraw-statistics.svg";

export default defineComponent({
  name: "LoginPage",

  components: {
    VoodooTuneLogo,
  },

  props: {
    /**
     * If the page needs the login button
     */
    showLogin: {
      type: Boolean,
      default: true,
    },

    /**
     * If true, display an error message that explain to the user
     * that he needs to contact the team to have access to VoodooTune.
     */
    unauthorized: {
      type: Boolean,
      default: false,
    },

    /**
     * If the page is visited from another part of the application
     */
    backButton: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { login } = useAuthentication();

    return {
      login,
      abtestScreenshotImage,
      segmentsScreenshotImage,
      duneImage,
      undrawCodeReviewImage,
      undrawStatisticsImage,
    };
  },
});
</script>

<style lang="scss" scoped>
.login-page {
  background-color: var(--voodootune-background-color);

  & > section {
    position: relative;
  }

  .dune-container {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;

    img {
      width: 100%;
      height: auto;
    }
  }

  .header {
    display: flex;
    padding: size("XXL") 15%;
    justify-content: space-between;

    > div {
      flex-basis: 50%;
      max-width: 550px;
    }

    .login-section {
      .voodootune-logo {
        margin-bottom: size("ML");
      }

      .login-sentence,
      .unauthorized-sentence {
        font-size: font("L");
        font-weight: 300;
        color: var(--voodootune-text-color);
      }

      .unauthorized-sentence {
        color: $red-alert;
      }

      .connect-button {
        margin-top: size("XXL");
      }
    }

    .login-screenshot-container {
      position: relative;
      min-height: 400px;

      & > * {
        position: absolute;
      }

      .login-screenshot-top {
        top: 0;
        left: 0;
      }

      .login-screenshot-bottom {
        bottom: 0;
        right: 0;
      }
    }
  }

  .login-characters-img {
    display: flex;
    top: -100px;
    justify-content: space-evenly;
    align-items: flex-end;
  }

  .use-cases {
    display: grid;
    grid-template-columns: repeat(2, 400px);
    column-gap: 155px;
    width: 100%;
    justify-items: left;
    justify-content: center;
    padding-bottom: size("XXL") * 2;

    .explanation {
      p {
        font-size: font("S");
        color: var(--voodootune-text-color);
      }
      h3 {
        font-size: font("L");
        font-weight: 300;
        color: $intense-blue;
      }
    }
  }

  .more-block {
    display: flex;
    flex-direction: column;
    align-items: center;

    .more-info {
      font-weight: 600;
      color: $intense-blue;
      margin-bottom: size("M");
    }

    .dropdown-icon {
      margin-bottom: 20px;
    }

    .use-case-sentence {
      font-size: font("L");
      font-weight: 400;
      color: var(--vd-base-secondary-text-color);
      margin-bottom: size("L");
    }
  }

  @include custom-breakpoint(0, 1500) {
    .header {
      flex-basis: 100%;
      flex-direction: column-reverse;

      .login-section {
        margin-bottom: size("L");
      }
    }
  }

  @include medium-screen {
    overflow: hidden;

    .use-cases {
      grid-template-columns: minmax(200px, 700px);
    }
    .dune-container {
      top: 300px;
    }
  }
}
</style>
